/**
 * Centralized Axios instance for all API communication with the serviceman backend.
 *
 * Configures base URL, auth token injection, multi-tenant environment routing,
 * and automatic token refresh on 401 responses.
 *
 * @remarks
 * **Base URL**: `http://localhost:9083` in development, `/api` in production.
 *
 * **Request interceptor** — injects two headers:
 * - `Authorization: Bearer <token>` from `localStorage.token`
 * - `X-Environment: <env>` for multi-tenant routing, resolved via priority chain:
 *   1. Explicit `config.env` string → sets header
 *   2. `config.env === null` → skips header (triggers backend aggregation across all envs)
 *   3. `config.env === undefined` → falls back to `localStorage.last_env`
 *
 * **Response interceptor** — handles:
 * - Stores `response.data.__source_env` to `localStorage.last_env` for fallback routing
 * - On 401 (non-auth endpoints): attempts token refresh via `/serviceman/auth/refresh-token`,
 *   then retries the original request once. On refresh failure, clears auth and redirects to `/login`.
 * - Auth endpoints (`validate-pin`, `request-pin`, `link-query`) are excluded from refresh logic
 *   since 401 there means invalid credentials, not an expired token.
 *
 * @module api/axios
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { refreshServicemanToken } from '../libs/tokenRefresh'

const isDev = process.env.NODE_ENV === 'development'

/**
 * Pre-configured Axios instance with auth and environment interceptors.
 *
 * Usage: pass `env` in the config object to control multi-tenant routing.
 *
 * @example
 * ```ts
 * // Query all environments (aggregation)
 * await api.post(URL, body, { env: null } as any)
 *
 * // Target specific environment
 * await api.get(URL, { env: 'app' } as any)
 *
 * // Use last known environment (default)
 * await api.get(URL)
 * ```
 */
const api = axios.create({
  baseURL: isDev ? 'http://localhost:9083' : '/api',
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor — injects Bearer token and X-Environment header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Auto-inject X-Environment header
    if (config.headers && !config.headers['X-Environment']) {
      // Check if environment was explicitly passed in config
      const configEnv = (config as any).env
      if (configEnv !== undefined) {
        // env explicitly set (could be null to skip header)
        if (configEnv !== null) {
          // Validate that env is a string or number
          if (typeof configEnv === 'string' || typeof configEnv === 'number') {
            config.headers['X-Environment'] = String(configEnv)
          } else {
            console.error('Invalid X-Environment value:', typeof configEnv, 'Expected string or number')
          }
        }
        // If env === null, don't add header (for aggregation endpoints)
      } else {
        // Fallback to last known environment from localStorage
        const lastEnv = localStorage.getItem('last_env')
        if (lastEnv) {
          config.headers['X-Environment'] = lastEnv
        }
      }
    }

    return config
  },
  error => Promise.reject(error)
)

// Response interceptor — stores __source_env and handles 401 with token refresh retry
api.interceptors.response.use(
  response => {
    // If response contains __source_env, store it as last known environment
    if (response.data?.__source_env) {
      localStorage.setItem('last_env', response.data.__source_env)
    }
    return response
  },
  async (error: AxiosError) => {
    // Type guard for AxiosError
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error)
    }

    const originalRequest: any = error.config

    // Skip token refresh for auth endpoints - 401 there means invalid credentials, not expired token
    const requestUrl = originalRequest?.url || ''
    const isAuthEndpoint =
      requestUrl.includes('/auth/validate-pin') || requestUrl.includes('/auth/request-pin') || requestUrl.includes('/auth/link-query')

    // If token expired and we haven't retried yet (but not on auth endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      try {
        // Use shared refresh function — deduplicates concurrent 401 retries
        // and handles logout/redirect consistently with tokenRefresh.ts
        const newToken = await refreshServicemanToken()

        if (newToken) {
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return axios(originalRequest)
        }

        // refreshServicemanToken handles redirect on failure, just reject
        return Promise.reject(error)
      } catch (refreshError) {
        // refreshServicemanToken handles cleanup and redirect internally
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default api
